import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FiMessageCircle,
  FiArrowLeft,
  FiLock,
  FiDownload,
  FiX,
  FiMoreVertical,
} from 'react-icons/fi';
import { apiJson, apiFetch } from '@/lib/api';
import type { Message, MessageListResponse, SelectedConversation, UserResponse, PresignedUrlResponse } from '@/lib/types';
import { conversationToTempCheckScope } from '@/lib/tempcheck-api';
import { MessageBubble } from './MessageBubble';
import { MessageInput, MessageInputHandle } from './MessageInput';

// Module-level set to track which conversations have been marked as read in this session
// This persists across component remounts to prevent duplicate API calls
const markedAsReadInSession = new Set<string>();

type ChatViewProps = {
  conversation: SelectedConversation;
  currentUser: UserResponse | undefined;
  onBack?: () => void;
  showHeader?: boolean;
  disputeInfo?: {
    itemName: string;
    isResolved: boolean;
  };
};

export function ChatView({
  conversation,
  currentUser,
  onBack,
  showHeader = true,
  disputeInfo,
}: ChatViewProps) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<MessageInputHandle>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const [messageText, setMessageText] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imageViewerUrl, setImageViewerUrl] = useState<string | null>(null);

  // Determine API endpoint based on conversation type
  const getMessagesEndpoint = useCallback(
    (beforeTimestamp?: string) => {
      const limit = 50;
      let baseUrl: string;

      if (conversation.type === 'main') {
        baseUrl = '/v1/messages';
      } else if (conversation.type === 'custom-chat') {
        baseUrl = `/v1/chats/${conversation.chatId}/messages`;
      } else if (conversation.type === 'expense-dispute') {
        baseUrl = `/v1/expense-disputes/${conversation.disputeId}/messages`;
      } else {
        baseUrl = `/v1/payment-disputes/${conversation.disputeId}/messages`;
      }

      const params = new URLSearchParams({ limit: String(limit) });
      if (beforeTimestamp) {
        params.set('before_timestamp', beforeTimestamp);
      }
      return `${baseUrl}?${params.toString()}`;
    },
    [conversation]
  );

  const getSendEndpoint = useCallback(() => {
    if (conversation.type === 'main') {
      return '/v1/messages';
    }
    if (conversation.type === 'custom-chat') {
      return `/v1/chats/${conversation.chatId}/messages`;
    }
    if (conversation.type === 'expense-dispute') {
      return `/v1/expense-disputes/${conversation.disputeId}/messages`;
    }
    return `/v1/payment-disputes/${conversation.disputeId}/messages`;
  }, [conversation]);

  // Query key for messages
  const queryKey = [
    'messages',
    conversation.type,
    conversation.type === 'main'
      ? 'main'
      : conversation.type === 'custom-chat'
        ? conversation.chatId
        : conversation.type === 'expense-dispute'
          ? conversation.disputeId
          : conversation.disputeId,
  ];

  // Fetch messages with infinite query for pagination
  const messagesQuery = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const url = getMessagesEndpoint(pageParam ?? undefined);
      return apiJson<MessageListResponse>(getToken, url);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more || lastPage.messages.length === 0) {
        return null;
      }
      const oldestMessage = lastPage.messages[lastPage.messages.length - 1];
      return oldestMessage.timestamp;
    },
    refetchInterval: 10000, // Poll every 10 seconds
  });

  // Mark as read when viewing
  useEffect(() => {
    if (conversation.type === 'main') {
      return;
    }

    const conversationKey =
      conversation.type === 'custom-chat'
        ? `${conversation.type}-${conversation.chatId}`
        : `${conversation.type}-${conversation.disputeId}`;

    if (markedAsReadInSession.has(conversationKey)) {
      return;
    }

    markedAsReadInSession.add(conversationKey);

    const markAsRead = async () => {
      try {
        if (conversation.type === 'expense-dispute') {
          await apiJson(getToken, `/v1/expense-disputes/${conversation.disputeId}/mark-read`, {
            method: 'POST',
          });
        } else if (conversation.type === 'payment-dispute') {
          await apiJson(getToken, `/v1/payment-disputes/${conversation.disputeId}/mark-read`, {
            method: 'POST',
          });
        } else if (conversation.type === 'custom-chat') {
          await apiJson(getToken, `/v1/chats/${conversation.chatId}/mark-read`, {
            method: 'POST',
          });
        }
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      } catch {
        markedAsReadInSession.delete(conversationKey);
      }
    };
    void markAsRead();
  }, [conversation, getToken, queryClient]);

  // Upload file to presigned URL
  const uploadFileToPresignedUrl = async (
    file: File,
    presignedUrl: string
  ): Promise<void> => {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Upload failed (${response.status})`);
    }
  };

  // Send message mutation
  const sendMutation = useMutation({
    mutationFn: async (data: { content: string; images: File[] }) => {
      const endpoint = getSendEndpoint();
      const messageResponse = await apiJson<Message>(getToken, endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_type: 'text', content: data.content }),
      });

      // Upload images if any
      if (data.images.length > 0 && messageResponse.id) {
        for (let i = 0; i < data.images.length; i++) {
          const file = data.images[i];
          try {
            const fileName = `attachment-${Date.now()}-${i}.${file.name.split('.').pop() || 'jpg'}`;
            const presignedResponse = await apiJson<PresignedUrlResponse>(
              getToken,
              `/v1/messages/${messageResponse.id}/attachments`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: fileName,
                  attachment_type: file.type,
                }),
              }
            );

            await uploadFileToPresignedUrl(file, presignedResponse.presigned_url);
          } catch (error) {
            console.error(`Failed to upload attachment ${i + 1}:`, error);
          }
        }
      }

      return messageResponse;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
      setMessageText('');
      setSelectedImages([]);
      inputRef.current?.focus();
    },
    onError: (err: Error) => {
      alert(err?.message ?? "Couldn't send message. Please try again.");
    },
  });

  const tempCheckScope = useMemo(
    () => conversationToTempCheckScope(conversation),
    [conversation]
  );

  const handleSend = useCallback(() => {
    const trimmed = messageText.trim();
    if ((!trimmed && selectedImages.length === 0) || sendMutation.isPending) return;
    const content = trimmed || (selectedImages.length > 0 ? '📎' : '');
    sendMutation.mutate({ content, images: selectedImages });
  }, [messageText, selectedImages, sendMutation]);

  const renameChatMutation = useMutation({
    mutationFn: async (title: string) => {
      if (conversation.type !== 'custom-chat') throw new Error();
      return apiJson(getToken, `/v1/chats/${conversation.chatId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim() }),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      if (conversation.type === 'custom-chat') {
        void queryClient.invalidateQueries({ queryKey: ['chat-room', conversation.chatId] });
      }
    },
  });

  const archiveChatMutation = useMutation({
    mutationFn: async () => {
      if (conversation.type !== 'custom-chat') throw new Error();
      const r = await apiFetch(getToken, `/v1/chats/${conversation.chatId}`, {
        method: 'DELETE',
      });
      if (!r.ok) throw new Error('Archive failed');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      if (conversation.type === 'custom-chat') {
        navigate({ to: '/app/chat' });
      }
    },
  });

  // Handle export
  const handleExport = async () => {
    try {
      let endpoint: string;
      if (conversation.type === 'main') {
        endpoint = '/v1/export/messages';
      } else if (conversation.type === 'custom-chat') {
        endpoint = `/v1/export/chats/${conversation.chatId}/messages`;
      } else if (conversation.type === 'expense-dispute') {
        endpoint = `/v1/export/expense-disputes/${conversation.disputeId}/messages`;
      } else {
        endpoint = `/v1/export/payment-disputes/${conversation.disputeId}/messages`;
      }

      const response = await apiFetch(getToken, endpoint, {
        headers: { Accept: 'application/pdf' },
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `messages-export-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Couldn't export messages. Please try again.");
    }
  };

  // Flatten messages from all pages
  const pages = messagesQuery.data?.pages ?? [];
  const messages = pages.flatMap((page) => page.messages);
  const hasMore = pages.length > 0 && pages[pages.length - 1]?.has_more;

  // Scroll to load more
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Since messages are displayed in reverse, "end" is at the top
    if (container.scrollTop < 100 && hasMore && !messagesQuery.isFetchingNextPage) {
      messagesQuery.fetchNextPage();
    }
  }, [hasMore, messagesQuery]);

  const [creatorMenuOpen, setCreatorMenuOpen] = useState(false);

  // Get title based on conversation type
  const getTitle = () => {
    if (conversation.type === 'main') {
      return 'Main Chat';
    }
    if (conversation.type === 'custom-chat') {
      return conversation.title;
    }
    return disputeInfo?.itemName ?? 'Dispute Chat';
  };

  const isDisabled =
    (disputeInfo?.isResolved ?? false) ||
    (conversation.type === 'custom-chat' && conversation.isArchived);

  return (
    <div className='flex flex-col h-full bg-sky-light'>
      {/* Header */}
      {showHeader && (
        <div className='flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-sky-light'>
          <div className='flex items-center gap-3'>
            {onBack && (
              <button
                onClick={onBack}
                className='w-8 h-8 flex items-center justify-center text-primary hover:bg-primary/10 rounded-full transition-colors'
              >
                <FiArrowLeft className='w-5 h-5' />
              </button>
            )}
            <div>
              <h1 className='font-semibold text-gray-900'>{getTitle()}</h1>
              {disputeInfo?.isResolved && (
                <p className='text-xs text-gray-500'>Dispute resolved</p>
              )}
              {conversation.type === 'custom-chat' && conversation.isArchived && (
                <p className='text-xs text-amber-800'>Archived</p>
              )}
            </div>
          </div>
          <div className='flex items-center gap-2'>
            {conversation.type === 'custom-chat' && conversation.isCreator && (
              <div className='relative'>
                <button
                  type='button'
                  onClick={() => setCreatorMenuOpen((o) => !o)}
                  className='w-8 h-8 flex items-center justify-center text-primary hover:bg-primary/10 rounded-full transition-colors'
                  title='More'
                >
                  <FiMoreVertical className='w-4 h-4' />
                </button>
                {creatorMenuOpen && (
                  <div className='absolute right-0 top-10 z-20 w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-md'>
                    <button
                      type='button'
                      className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50'
                      onClick={() => {
                        setCreatorMenuOpen(false);
                        if (conversation.type !== 'custom-chat') return;
                        const t = window.prompt('Rename chat', conversation.title);
                        if (t?.trim()) {
                          void renameChatMutation.mutate(t);
                        }
                      }}
                    >
                      Rename
                    </button>
                    <button
                      type='button'
                      className='w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50'
                      onClick={() => {
                        setCreatorMenuOpen(false);
                        if (window.confirm('Archive this chat? No new messages can be sent.')) {
                          void archiveChatMutation.mutate();
                        }
                      }}
                    >
                      Archive
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => {/* Show security modal */}}
              className='w-8 h-8 flex items-center justify-center text-primary hover:bg-primary/10 rounded-full transition-colors'
              title='Secure messaging'
            >
              <FiLock className='w-4 h-4' />
            </button>
            <button
              onClick={handleExport}
              className='w-8 h-8 flex items-center justify-center text-primary hover:bg-primary/10 rounded-full transition-colors'
              title='Export messages'
            >
              <FiDownload className='w-4 h-4' />
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className='flex-1 overflow-y-auto px-4 py-3 flex flex-col-reverse'
      >
        {messagesQuery.isLoading ? (
          <div className='flex items-center justify-center py-12'>
            <div className='animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full' />
          </div>
        ) : messagesQuery.isError ? (
          <div className='flex flex-col items-center justify-center py-12 text-center'>
            <div className='w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3'>
              <FiMessageCircle className='w-6 h-6 text-gray-400' />
            </div>
            <p className='text-gray-900 font-semibold'>Couldn't load messages</p>
            <p className='text-gray-500 text-sm mt-1'>
              <button onClick={() => messagesQuery.refetch()} className='text-primary hover:underline'>
                Try again
              </button>
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-12 text-center'>
            <div className='w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3'>
              <FiMessageCircle className='w-6 h-6 text-gray-400' />
            </div>
            <p className='text-gray-900 font-semibold'>No messages yet</p>
            <p className='text-gray-500 text-sm mt-1'>Start a conversation with your co-parent</p>
          </div>
        ) : (
          <>
            {/* Loading more indicator */}
            {messagesQuery.isFetchingNextPage && (
              <div className='flex items-center justify-center py-3 gap-2'>
                <div className='animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full' />
                <span className='text-sm text-gray-500'>Loading older messages...</span>
              </div>
            )}

            {/* Messages */}
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                currentUserId={currentUser?.id ?? 0}
                onImagePress={setImageViewerUrl}
              />
            ))}
          </>
        )}
      </div>

      {/* Input */}
      <MessageInput
        ref={inputRef}
        messageText={messageText}
        onMessageTextChange={setMessageText}
        selectedImages={selectedImages}
        onSelectedImagesChange={setSelectedImages}
        onSend={handleSend}
        isSending={sendMutation.isPending}
        disabled={isDisabled}
        tempCheckScope={tempCheckScope}
      />

      {/* Image viewer modal */}
      {imageViewerUrl && (
        <div
          className='fixed inset-0 z-50 bg-black/95 flex items-center justify-center'
          onClick={() => setImageViewerUrl(null)}
        >
          <button
            className='absolute top-4 right-4 w-11 h-11 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors'
            onClick={() => setImageViewerUrl(null)}
          >
            <FiX className='w-6 h-6' />
          </button>
          <img
            src={imageViewerUrl}
            alt='Full size'
            className='max-w-full max-h-full object-contain'
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
